import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeftRight, X } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import usePulseLocale from "./i18n";
import { tokens, fmtCompact, fmtNum } from "./theme";
import { SkeletonCard, SourceChip, ChartTooltipContent } from "./components";
import { pulseGet } from "./api";

export const cmpColors = (dm) => ({ a: dm ? "#33C6D4" : "#006874", b: dm ? "#F0717B" : "#C8333F" });
const fold = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

/** Typeahead over the catalog (≈600 cities). Selected value shown as "Name · CC". */
const CityPicker = ({ slot, value, catalog, onPick, dm, exclude }) => {
  const { t } = usePulseLocale();
  const T = tokens(dm);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const box = useRef(null);
  const sel = useMemo(() => catalog.find((c) => c.city === value), [catalog, value]);
  const selLabel = sel ? `${sel.name} · ${sel.country_iso2}` : "";
  const matches = useMemo(() => {
    const f = fold(q.trim());
    if (!f) return [];
    return catalog.filter((c) => c.city !== exclude && (fold(c.name).includes(f) || fold(c.country_iso2) === f)).slice(0, 8);
  }, [q, catalog, exclude]);
  useEffect(() => { setActive(0); }, [matches]);
  const close = () => { setOpen(false); setQ(""); };
  const pick = (c) => { onPick(c.city); close(); };
  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(matches.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter" && matches[active]) { e.preventDefault(); pick(matches[active]); }
    else if (e.key === "Escape") { e.preventDefault(); close(); }
  };
  useEffect(() => {
    const onDoc = (e) => { if (box.current && !box.current.contains(e.target)) close(); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  return (
    <div ref={box} className="relative flex-1 min-w-[200px]" data-testid={`cities-pick-${slot}`}>
      <label className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: cmpColors(dm)[slot] }}>{t(`cities_compare_${slot}`)}</label>
      <div className="flex items-center gap-1" style={{ border: `1px solid ${T.border}`, background: T.surface, borderRadius: 2 }}>
        <input type="text" className="flex-1 min-w-0 text-xs px-2 py-1.5 bg-transparent outline-none" data-testid={`cities-pick-${slot}-input`}
          placeholder={t("cities_compare_search")} value={open ? q : (selLabel || q)}
          onFocus={() => { setOpen(true); setQ(""); }} onClick={() => { if (!open) { setOpen(true); setQ(""); } }}
          onChange={(e) => { const v = e.target.value; setQ(!open && selLabel && v.startsWith(selLabel) ? v.slice(selLabel.length) : v); setOpen(true); }} onKeyDown={onKey}
          onBlur={(e) => { if (!e.relatedTarget || !e.relatedTarget.closest("[role=listbox]")) close(); }}
          style={{ color: T.text }} role="combobox" aria-autocomplete="list" aria-expanded={open} aria-controls={`cities-pick-${slot}-list`}
          aria-activedescendant={open && matches[active] ? `cities-pick-${slot}-opt-${matches[active].city}` : undefined} />
        {value && (
          <button type="button" aria-label={t("cities_compare_clear")} data-testid={`cities-pick-${slot}-clear`} className="px-1.5" style={{ color: T.muted }}
            onClick={() => { onPick(null); setQ(""); }}><X className="w-3.5 h-3.5" aria-hidden="true" /></button>
        )}
      </div>
      {open && q.trim() && (
        <ul id={`cities-pick-${slot}-list`} className="absolute z-30 left-0 right-0 mt-1 max-h-64 overflow-auto shadow-lg" role="listbox" data-testid={`cities-pick-${slot}-list`}
          style={{ background: T.surface, border: `1px solid ${T.border}` }}>
          {matches.length === 0 && <li className="text-xs px-2 py-1.5" style={{ color: T.muted }}>{t("cities_compare_no_match")}</li>}
          {matches.map((c, i) => (
            <li key={c.city} id={`cities-pick-${slot}-opt-${c.city}`} role="option" aria-selected={i === active}
              style={{ background: i === active ? T.surface2 : "transparent" }}>
              <button type="button" tabIndex={-1} className="w-full text-left text-xs px-2 py-1.5 flex items-center justify-between gap-2 hover:opacity-80"
                data-testid={`cities-pick-${slot}-option-${c.city}`} style={{ color: T.text }}
                onMouseDown={(e) => e.preventDefault()} onMouseEnter={() => setActive(i)} onClick={() => pick(c)}>
                <span className="font-semibold truncate">{c.name} <span style={{ color: T.muted }}>· {c.country_iso2}</span></span>
                <span className="tabular-nums text-[10px]" style={{ color: T.muted }}>#{c.rank}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Metric = ({ label, a, b, fmt, delta, dm, testid }) => {
  const T = tokens(dm);
  return (
    <tr style={{ borderTop: `1px solid ${T.border}` }} data-testid={testid}>
      <th scope="row" className="text-left text-[11px] font-semibold py-2 pr-2" style={{ color: T.muted }}>{label}</th>
      <td className="py-2 px-2 text-right tabular-nums font-semibold whitespace-nowrap" style={{ color: T.text }}>{fmt(a)}</td>
      <td className="py-2 px-2 text-right tabular-nums font-semibold whitespace-nowrap" style={{ color: T.text }}>{fmt(b)}</td>
      <td className="py-2 pl-2 text-right tabular-nums text-[11px] whitespace-nowrap" style={{ color: T.muted }}>{delta}</td>
    </tr>
  );
};

const TrendChart = ({ title, data, keyA, keyB, nameA, nameB, fmt, dm, testid, yDomain }) => {
  const T = tokens(dm);
  return (
    <div data-testid={testid}>
      <div className="text-[11px] font-semibold mb-1" style={{ color: T.text }}>{title}</div>
      <div style={{ height: 190 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: -12 }}>
            <CartesianGrid stroke={T.border} vertical={false} />
            <XAxis dataKey="year" tick={{ fontSize: 10, fill: T.muted }} tickLine={false} axisLine={{ stroke: T.border }} />
            <YAxis tick={{ fontSize: 10, fill: T.muted }} tickLine={false} axisLine={false} width={48} domain={yDomain} tickFormatter={fmt} />
            <Tooltip content={<ChartTooltipContent dm={dm} formatter={fmt} />} />
            <Line type="monotone" dataKey={keyA} name={nameA} stroke={cmpColors(dm).a} strokeWidth={2.2} dot={{ r: 3 }} connectNulls={false} isAnimationActive animationDuration={400} />
            <Line type="monotone" dataKey={keyB} name={nameB} stroke={cmpColors(dm).b} strokeWidth={2.2} dot={{ r: 3 }} connectNulls={false} isAnimationActive animationDuration={400} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export const CityComparePanel = ({ a, b, onChange, catalog, dm, since = "2019" }) => {
  const { t, lang } = usePulseLocale();
  const T = tokens(dm);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!a || !b) { setData(null); return undefined; }
    let on = true;
    setLoading(true);
    pulseGet("cities/compare", { a, b, since })
      .then((d) => on && setData(d)).catch(() => on && setData(null)).finally(() => on && setLoading(false));
    return () => { on = false; };
  }, [a, b, since]);

  const [ca, cb] = data?.cities || [];
  const latest = data?.latest;
  const la = ca?.found ? ca.series[ca.series.length - 1] : null;
  const lb = cb?.found ? cb.series[cb.series.length - 1] : null;
  const prev = data?.years?.length > 1 ? data.years[data.years.length - 2] : null;
  const pa = ca?.found && prev ? ca.series[ca.series.length - 2] : null;
  const pb = cb?.found && prev ? cb.series[cb.series.length - 2] : null;
  const yoy = (cur, pr) => (cur?.nights_total && pr?.nights_total ? (cur.nights_total / pr.nights_total - 1) * 100 : null);
  const trend = useMemo(() => (data?.years || []).map((y, i) => ({
    year: y,
    nightsA: ca?.found && ca.series[i].nights_total != null ? ca.series[i].nights_total / 1e6 : null,
    nightsB: cb?.found && cb.series[i].nights_total != null ? cb.series[i].nights_total / 1e6 : null,
    shareA: ca?.found ? ca.series[i].foreign_share_pct : null,
    shareB: cb?.found ? cb.series[i].foreign_share_pct : null,
  })), [data, ca, cb]);

  const num = (v, d = 1) => (v === null || v === undefined ? "—" : fmtNum(v, lang, d));
  const pct = (v) => (v === null || v === undefined ? "—" : `${fmtNum(v, lang, 1)}%`);
  const dlt = (x, y, suffix = "", d = 1) => (x == null || y == null ? "—" : `${x - y >= 0 ? "+" : "−"}${fmtNum(Math.abs(x - y), lang, d)}${suffix}`);
  const name = (c) => (c?.found ? `${c.name} · ${c.country_iso2}` : "—");

  return (
    <section className="mb-16" data-testid="cities-compare" aria-labelledby="cities-compare-title">
      <h2 id="cities-compare-title" className="pulse-serif text-lg font-semibold mb-1" style={{ color: T.text }}>{t("cities_compare_title")}</h2>
      <p className="text-xs mb-3 max-w-3xl" style={{ color: T.muted }}>{t("cities_compare_hint", { from: since })}</p>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <CityPicker slot="a" value={a} exclude={b} catalog={catalog} dm={dm} onPick={(v) => onChange(v, b)} />
        <button type="button" onClick={() => onChange(b, a)} disabled={!a && !b} data-testid="cities-compare-swap" aria-label={t("cities_compare_swap")}
          className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 disabled:opacity-40 self-end" style={{ background: T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: 2 }}>
          <ArrowLeftRight className="w-3.5 h-3.5" aria-hidden="true" /> {t("cities_compare_swap")}
        </button>
        <CityPicker slot="b" value={b} exclude={a} catalog={catalog} dm={dm} onPick={(v) => onChange(a, v)} />
      </div>

      {!a || !b ? <p className="text-xs py-6" style={{ color: T.muted }} data-testid="cities-compare-empty">{t("cities_compare_empty")}</p>
        : loading && !data ? <SkeletonCard h={320} dm={dm} />
        : !data ? null : (
          <div className="p-4 md:p-5" style={{ background: T.surface, border: `1px solid ${T.border}` }} data-testid="cities-compare-result">
            {[ca, cb].filter((c) => c && !c.found).map((c) => (
              <p key={c.city} className="text-xs mb-3" style={{ color: T.down }} data-testid="cities-compare-unknown">{t("cities_compare_unknown", { code: c.city })}</p>
            ))}
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-6">
              <table className="w-full text-xs" data-testid="cities-compare-table">
                <thead>
                  <tr>
                    <th className="text-left text-[10px] font-bold uppercase tracking-wider pb-2" style={{ color: T.muted }}>{t("cities_compare_metric", { year: latest })}</th>
                    <th className="text-right text-[11px] font-bold pb-2 px-2 whitespace-nowrap" style={{ color: cmpColors(dm).a }} data-testid="cities-compare-name-a">{name(ca)}</th>
                    <th className="text-right text-[11px] font-bold pb-2 px-2 whitespace-nowrap" style={{ color: cmpColors(dm).b }} data-testid="cities-compare-name-b">{name(cb)}</th>
                    <th className="text-right text-[10px] font-bold uppercase tracking-wider pb-2 pl-2" style={{ color: T.muted }}>{t("cities_compare_delta")}</th>
                  </tr>
                </thead>
                <tbody>
                  <Metric dm={dm} testid="cities-compare-rank" label={`${t("cities_compare_rank")}${la?.universe ? ` (${t("cities_compare_of", { n: la.universe })})` : ""}`} a={la?.rank} b={lb?.rank}
                    fmt={(v) => (v ? `#${v}` : "—")} delta={la?.rank && lb?.rank ? `${la.rank - lb.rank > 0 ? "+" : ""}${la.rank - lb.rank}` : "—"} />
                  <Metric dm={dm} testid="cities-compare-nights" label={t("cities_compare_nights")} a={la?.nights_total} b={lb?.nights_total}
                    fmt={(v) => (v == null ? "—" : fmtCompact(v, lang))} delta={dlt(la?.nights_total && la.nights_total / 1e6, lb?.nights_total && lb.nights_total / 1e6, " M")} />
                  <Metric dm={dm} testid="cities-compare-yoy" label={t("cities_compare_yoy", { prev: prev || "—" })} a={yoy(la, pa)} b={yoy(lb, pb)}
                    fmt={(v) => (v == null ? "—" : `${v >= 0 ? "▲" : "▼"} ${fmtNum(Math.abs(v), lang, 1)}%`)} delta={dlt(yoy(la, pa), yoy(lb, pb), " pp")} />
                  <Metric dm={dm} testid="cities-compare-share" label={t("cities_compare_share")} a={la?.foreign_share_pct} b={lb?.foreign_share_pct} fmt={pct} delta={dlt(la?.foreign_share_pct, lb?.foreign_share_pct, " pp")} />
                  <Metric dm={dm} testid="cities-compare-npr" label={t("cities_compare_npr")} a={la?.nights_per_resident} b={lb?.nights_per_resident} fmt={(v) => num(v, 1)} delta={dlt(la?.nights_per_resident, lb?.nights_per_resident)} />
                  <Metric dm={dm} testid="cities-compare-pop" label={`${t("cities_compare_pop")}${la?.population_year || lb?.population_year ? ` (${la?.population_year || "—"} / ${lb?.population_year || "—"})` : ""}`}
                    a={la?.population} b={lb?.population} fmt={(v) => (v == null ? "—" : fmtCompact(v, lang))} delta={dlt(la?.population && la.population / 1e6, lb?.population && lb.population / 1e6, " M", 2)} />
                </tbody>
              </table>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <TrendChart dm={dm} testid="cities-compare-trend-nights" title={t("cities_compare_trend_nights", { from: data.years[0] })} data={trend}
                  keyA="nightsA" keyB="nightsB" nameA={ca?.name || "A"} nameB={cb?.name || "B"} fmt={(v) => (v == null ? "—" : fmtNum(v, lang, 1))} yDomain={[0, "auto"]} />
                <TrendChart dm={dm} testid="cities-compare-trend-share" title={t("cities_compare_trend_share", { from: data.years[0] })} data={trend}
                  keyA="shareA" keyB="shareB" nameA={ca?.name || "A"} nameB={cb?.name || "B"} fmt={(v) => (v == null ? "—" : fmtNum(v, lang, 0))} yDomain={[0, 100]} />
              </div>
            </div>
            <p className="text-[11px] leading-relaxed mt-4 max-w-3xl" style={{ color: T.muted }} data-testid="cities-compare-note">{t("cities_compare_note")}</p>
            <div className="flex flex-col gap-1.5 mt-3">
              {(data.sources || []).map((s) => <SourceChip key={s.dataset_code} source={s.source_org} dataset={s.dataset_code} period={s.period} dm={dm} testid={`cities-compare-source-${s.dataset_code}`} />)}
            </div>
          </div>
        )}
    </section>
  );
};
